import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Cabine } from "../componentes/FerramentaUi";
import { CAMPO } from "../componentes/ui";
import {
  Alternador, BOTAO_SECUNDARIO, Campo, Chip, Dialogo, EtapaProgresso, IconeAlca, IconeBusca, IconeEstrela, IconeMais,
  IconeMarcador, ProximaAcaoInline,
} from "../componentes/PeritoUi";
import { formatarData } from "../lib/perito/datas";
import { formatBRL, PRIORIDADE_LABEL, PRIORIDADE_TOM, plural } from "../lib/perito/format";
import { ETAPAS, ETAPA_COR, ETAPA_LABEL, TOTAL_ETAPAS, etapaNumDe, type EtapaNum } from "../lib/perito/pipeline";
import { usePerito } from "../lib/perito/store";
import type { Pericia, Prioridade } from "../lib/perito/tipos";

// As perícias (000385), portadas de `src/routes/_authenticated/pericias.index.tsx` da Central do Perito: os
// cards, a tabela e o kanban pelas 10 etapas, com as pílulas de etapa (cada uma com a contagem sobre os
// OUTROS filtros), a busca, o tipo, a prioridade e os filtros salvos. A visualização escolhida fica no
// navegador (conveniência de quem vê, como lá). Desde a fase B a lista mora em `/pericias` (o endereço da
// ferramenta abre o painel), e o filtro salvo chega do painel pela query (`?filtro=<id>`).

const RAIZ = "/ferramenta/central-do-perito";
type Visao = "cards" | "tabela" | "kanban";
const CHAVE_DA_VISAO = "perito.visaoDasPericias";
const VISOES = [
  { id: "cards", rotulo: "Cards" },
  { id: "tabela", rotulo: "Tabela" },
  { id: "kanban", rotulo: "Kanban" },
] as const;

function visaoGuardada(): Visao {
  try {
    const v = localStorage.getItem(CHAVE_DA_VISAO);
    return v === "tabela" || v === "kanban" ? v : "cards";
  } catch {
    return "cards";
  }
}

function totais(p: Pericia) {
  const total = p.honorarios.reduce((a, h) => a + h.valor, 0);
  const recebido = p.honorarios.reduce((a, h) => a + h.recebido, 0);
  return { total, recebido, pct: total ? Math.min(100, (recebido / total) * 100) : 0 };
}

/** O nome com que o aluno reconhece a perícia: o apelido, senão o tipo. */
export function nomeDaPericia(p: Pericia): string {
  return p.apelido || p.tipoPericia || "Tipo não informado";
}

/** A pílula de etapa do filtro: o ponto da fase, o nome e a contagem. */
function PilulaDeEtapa({ ativa, aoClicar, children, contagem, cor }: {
  ativa: boolean; aoClicar: () => void; children: ReactNode; contagem: number; cor?: string;
}) {
  return (
    <button type="button" onClick={aoClicar} aria-pressed={ativa}
      className={`inline-flex min-h-9 shrink-0 items-center gap-2 rounded-full border px-3.5 text-sm font-semibold whitespace-nowrap transition ${
        ativa ? "border-acento bg-acento text-acento-tinta" : "border-borda bg-superficie text-cinza hover:border-acento hover:text-titulo"
      }`}
      data-pilula-etapa>
      {cor && <span aria-hidden="true" className={`size-2 rounded-full ${cor}`} />}
      {children}
      <span className={`rounded-full px-1.5 text-xs tabular-nums ${ativa ? "bg-white/20" : "bg-chip"}`} data-contagem>{contagem}</span>
    </button>
  );
}

function Favorito({ p }: { p: Pericia }) {
  const { toggleFavorito, podeEscrever } = usePerito();
  return (
    <button type="button" onClick={() => toggleFavorito(p.id)} disabled={!podeEscrever}
      aria-label={p.favorito ? "Tirar dos favoritos" : "Marcar como favorita"} aria-pressed={p.favorito}
      className="relative z-10 grid size-9 shrink-0 place-items-center rounded-full text-cinza transition hover:bg-chip hover:text-laranja-texto disabled:opacity-55">
      <IconeEstrela cheia={p.favorito} className={`size-4 ${p.favorito ? "text-laranja-texto" : ""}`} />
    </button>
  );
}

export default function PeritoPericias() {
  const s = usePerito();
  const { pericias, filtrosSalvos } = s;
  const [visao, setVisao] = useState<Visao>(visaoGuardada);
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("todos");
  const [etapaFiltro, setEtapaFiltro] = useState<"todos" | EtapaNum>("todos");
  const [prioridade, setPrioridade] = useState<"todas" | Prioridade>("todas");
  const [salvarAberto, setSalvarAberto] = useState(false);
  const [nomeFiltro, setNomeFiltro] = useState("");
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [alvo, setAlvo] = useState<EtapaNum | null>(null);
  const [recado, setRecado] = useState<string | null>(null);

  const trocarVisao = (v: Visao) => {
    setVisao(v);
    try { localStorage.setItem(CHAVE_DA_VISAO, v); } catch { /* sem armazenamento, a escolha vale só agora */ }
  };

  const tipos = useMemo(() => Array.from(new Set(pericias.map((p) => p.tipoPericia).filter(Boolean))).sort((a, b) => a.localeCompare(b)), [pericias]);

  /** Os filtros MENOS a etapa: é a base das contagens das pílulas. */
  const base = useMemo(() => {
    const t = q.trim().toLowerCase();
    return pericias.filter((p) => {
      if (prioridade !== "todas" && p.prioridade !== prioridade) return false;
      if (tipo !== "todos" && p.tipoPericia !== tipo) return false;
      if (!t) return true;
      return [p.numeroProcesso, p.apelido ?? "", p.tribunal, p.vara, p.tipoPericia, p.proximaAcao ?? ""].some((c) => c.toLowerCase().includes(t));
    });
  }, [pericias, q, prioridade, tipo]);

  const contagens = useMemo(() => {
    const m = new Map<EtapaNum, number>(ETAPAS.map((e) => [e, 0]));
    for (const p of base) m.set(etapaNumDe(p), (m.get(etapaNumDe(p)) ?? 0) + 1);
    return m;
  }, [base]);

  const lista = etapaFiltro === "todos" ? base : base.filter((p) => etapaNumDe(p) === etapaFiltro);
  const filtroAtivo = !!q || prioridade !== "todas" || tipo !== "todos" || etapaFiltro !== "todos";
  const limpar = () => { setQ(""); setPrioridade("todas"); setTipo("todos"); setEtapaFiltro("todos"); };
  const aplicar = (f: (typeof filtrosSalvos)[number]) => {
    setQ(f.filtros.busca ?? "");
    setPrioridade((f.filtros.prioridade as Prioridade | undefined) ?? "todas");
    setTipo(f.filtros.tipoPericia ?? "todos");
  };
  // O filtro salvo que o painel manda (`?filtro=`) é aplicado UMA vez e sai do endereço: se ficasse, mexer num
  // campo e recarregar devolveria o filtro por cima do que a pessoa escolheu.
  const [query, setQuery] = useSearchParams();
  const filtroDaQuery = query.get("filtro");
  useEffect(() => {
    if (!filtroDaQuery) return;
    const f = filtrosSalvos.find((x) => x.id === filtroDaQuery);
    if (f) aplicar(f);
    const resto = new URLSearchParams(query);
    resto.delete("filtro");
    setQuery(resto, { replace: true });
  }, [filtroDaQuery]);

  const salvar = () => {
    if (!nomeFiltro.trim()) return;
    s.addFiltroSalvo(nomeFiltro.trim(), {
      busca: q || undefined,
      prioridade: prioridade !== "todas" ? prioridade : undefined,
      tipoPericia: tipo !== "todos" ? tipo : undefined,
    });
    setNomeFiltro("");
    setSalvarAberto(false);
    setRecado("Filtro salvo.");
  };
  const soltar = (etapa: EtapaNum) => {
    if (arrastando) {
      s.moverEtapa(arrastando, etapa);
      setRecado(`Movida para ${ETAPA_LABEL[etapa]}.`);
    }
    setArrastando(null);
    setAlvo(null);
  };

  const descricao =
    pericias.length === 0
      ? "Nenhuma perícia cadastrada ainda. Cadastre a primeira, e as tarefas da etapa em que ela começa entram sozinhas."
      : filtroAtivo
        ? `Mostrando ${lista.length} de ${plural(pericias.length, "perícia", "perícias")} com os filtros escolhidos.`
        : `${plural(pericias.length, "perícia", "perícias")}, acompanhadas pelas 10 etapas do processo judicial.`;

  return (
    <div className="flex flex-col gap-5" data-pericias>
      <Cabine rotulo="Central do Perito" titulo="Suas perícias" descricao={descricao}
        acao={s.podeEscrever && (
          <Link to={`${RAIZ}/pericias/nova`} className="pill-cta inline-flex min-h-11 items-center gap-1.5 px-5 text-sm" data-nova-pericia>
            <IconeMais /> Nova perícia
          </Link>
        )} />

      {recado && <p role="status" className="rounded-xl bg-aviso-fundo p-3 text-sm text-aviso-tinta">{recado}</p>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2" data-filtros-salvos>
          {filtrosSalvos.length > 0 && <span className="text-[11px] font-bold tracking-[0.14em] text-cinza uppercase">Filtros salvos</span>}
          {filtrosSalvos.map((f) => (
            <span key={f.id} className="inline-flex min-h-9 items-center gap-1 rounded-full border border-borda bg-superficie pr-1 pl-3 text-sm font-semibold text-titulo">
              <button type="button" onClick={() => aplicar(f)} className="flex items-center gap-1.5 hover:text-link" data-aplicar-filtro={f.nome}>
                <IconeMarcador className="size-3.5 text-laranja-texto" /> {f.nome}
              </button>
              {s.podeEscrever && (
                <button type="button" aria-label={`Excluir o filtro ${f.nome}`} onClick={() => s.deleteFiltroSalvo(f.id)}
                  className="grid size-7 place-items-center rounded-full text-cinza hover:bg-chip hover:text-erro-tinta">×</button>
              )}
            </span>
          ))}
        </div>
        <Alternador rotulo="Visualização" opcoes={VISOES} valor={visao} aoMudar={trocarVisao} />
      </div>

      <div className="-mx-1 overflow-x-auto px-1 pb-1">
        <div className="flex min-w-max gap-1.5" role="group" aria-label="Filtrar pela etapa">
          <PilulaDeEtapa ativa={etapaFiltro === "todos"} aoClicar={() => setEtapaFiltro("todos")} contagem={base.length}>Todas</PilulaDeEtapa>
          {ETAPAS.map((e) => (
            <PilulaDeEtapa key={e} ativa={etapaFiltro === e} aoClicar={() => setEtapaFiltro(e)} contagem={contagens.get(e) ?? 0} cor={ETAPA_COR[e]}>
              {ETAPA_LABEL[e]}
            </PilulaDeEtapa>
          ))}
        </div>
      </div>

      <div className="surface flex flex-wrap items-center gap-2 p-3">
        <div className="relative min-w-55 flex-1">
          <IconeBusca className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-cinza" />
          <input className={`${CAMPO} pl-10`} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por número, apelido, tribunal, vara, tipo ou próxima ação…" aria-label="Buscar perícias" data-busca />
        </div>
        <select className={`${CAMPO} sm:w-55`} value={tipo} onChange={(e) => setTipo(e.target.value)} aria-label="Tipo de perícia">
          <option value="todos">Todos os tipos</option>
          {tipos.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className={`${CAMPO} sm:w-55`} value={prioridade} onChange={(e) => setPrioridade(e.target.value as typeof prioridade)} aria-label="Prioridade">
          <option value="todas">Todas as prioridades</option>
          {(Object.keys(PRIORIDADE_LABEL) as Prioridade[]).map((p) => <option key={p} value={p}>{PRIORIDADE_LABEL[p]}</option>)}
        </select>
        {filtroAtivo && (
          <>
            {s.podeEscrever && <button type="button" className={BOTAO_SECUNDARIO} onClick={() => setSalvarAberto(true)} data-salvar-filtro><IconeMarcador /> Salvar filtro</button>}
            <button type="button" className="min-h-10 px-3 text-sm font-semibold text-link hover:underline" onClick={limpar}>Limpar</button>
          </>
        )}
      </div>

      {visao === "cards" && (lista.length === 0 ? (
        <div className="surface p-8 text-center text-sm text-cinza">Nenhuma perícia encontrada. Ajuste a busca e os filtros, ou cadastre uma nova.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-cards>
          {lista.map((p) => {
            const t = totais(p);
            return (
              // O cartão inteiro abre a perícia (o link do título se estica por cima dele), e a estrela e a
              // próxima ação continuam clicáveis por cima, com `relative z-10`: link dentro de link é inválido.
              <article key={p.id} className="surface porta relative flex flex-col gap-3 p-5" data-pericia={p.id}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link to={`${RAIZ}/pericias/${p.id}`} className="block truncate font-display text-base font-bold text-titulo after:absolute after:inset-0 after:content-['']">
                      {nomeDaPericia(p)}
                    </Link>
                    <span className="block truncate text-xs text-cinza tabular-nums">{p.numeroProcesso || "(sem número)"}</span>
                  </div>
                  <Favorito p={p} />
                </div>
                <p className="-mt-1 truncate text-sm text-cinza">{[p.tribunal, p.vara, p.apelido ? p.tipoPericia : ""].filter(Boolean).join(" · ")}</p>
                <EtapaProgresso pericia={p} />
                <ProximaAcaoInline pericia={p} />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Chip tom={PRIORIDADE_TOM[p.prioridade]}>{PRIORIDADE_LABEL[p.prioridade]}</Chip>
                  {t.total > 0 && <span className="text-xs text-cinza tabular-nums"><span className="font-semibold text-aviso-tinta">{formatBRL(t.recebido)}</span> de {formatBRL(t.total)}</span>}
                </div>
                <div className="mt-auto flex items-center justify-between border-t border-borda pt-3 text-xs text-cinza">
                  <span>Atualizada em {formatarData(p.atualizadoEm)}</span>
                  <span aria-hidden="true" className="font-semibold text-link">Abrir <span className="text-laranja-texto">→</span></span>
                </div>
              </article>
            );
          })}
        </div>
      ))}

      {visao === "tabela" && (
        <div className="surface overflow-x-auto" data-tabela>
          <table className="w-full min-w-245 text-sm">
            <thead className="border-b border-borda text-left">
              <tr className="text-[11px] font-bold tracking-[0.12em] text-cinza uppercase">
                <th className="w-10 px-4 py-3"><span className="sr-only">Favorita</span></th>
                <th className="px-3 py-3">Perícia</th>
                <th className="px-3 py-3">Tribunal / Vara</th>
                <th className="w-55 px-3 py-3">Etapa</th>
                <th className="px-3 py-3">Próxima ação</th>
                <th className="px-3 py-3">Prioridade</th>
                <th className="px-3 py-3 text-right">Honorários</th>
                <th className="px-4 py-3">Atualizada</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borda">
              {lista.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-cinza">Nenhuma perícia encontrada.</td></tr>}
              {lista.map((p) => (
                <tr key={p.id} className="align-top" data-pericia={p.id}>
                  <td className="px-4 py-3"><Favorito p={p} /></td>
                  <td className="px-3 py-3">
                    <Link to={`${RAIZ}/pericias/${p.id}`} className="block font-semibold text-titulo hover:text-link hover:underline">{nomeDaPericia(p)}</Link>
                    <span className="block text-xs text-cinza tabular-nums">{p.numeroProcesso || "(sem número)"}</span>
                  </td>
                  <td className="px-3 py-3"><div className="font-medium text-tinta">{p.tribunal}</div><div className="text-xs text-cinza">{p.vara}</div></td>
                  <td className="px-3 py-3"><EtapaProgresso pericia={p} /></td>
                  <td className="min-w-55 px-3 py-3"><ProximaAcaoInline pericia={p} /></td>
                  <td className="px-3 py-3"><Chip tom={PRIORIDADE_TOM[p.prioridade]}>{PRIORIDADE_LABEL[p.prioridade]}</Chip></td>
                  <td className="px-3 py-3 text-right font-semibold whitespace-nowrap text-titulo tabular-nums">{formatBRL(totais(p).total)}</td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap text-cinza tabular-nums">{formatarData(p.atualizadoEm)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {visao === "kanban" && (
        <div className="-mx-1 overflow-x-auto px-1 pb-3" data-kanban>
          <div className="flex min-w-max gap-3">
            {ETAPAS.map((etapa) => {
              const itens = base.filter((p) => etapaNumDe(p) === etapa);
              return (
                <section key={etapa} aria-label={`Etapa ${etapa}: ${ETAPA_LABEL[etapa]}`} data-coluna={etapa}
                  onDragOver={(e) => { if (!s.podeEscrever) return; e.preventDefault(); setAlvo(etapa); }}
                  onDragLeave={() => setAlvo((a) => (a === etapa ? null : a))}
                  onDrop={(e) => { e.preventDefault(); soltar(etapa); }}
                  className={`w-65 shrink-0 overflow-hidden rounded-cf-lg border transition-colors ${alvo === etapa ? "border-laranja-texto bg-chip" : "border-borda bg-fundo"}`}>
                  <div className={`h-1 ${ETAPA_COR[etapa]}`} />
                  <div className="flex items-center justify-between gap-2 px-3.5 pt-3 pb-2">
                    <span className="min-w-0 truncate text-sm font-bold text-titulo">
                      <span aria-hidden="true" className="mr-1.5 font-display text-laranja-texto tabular-nums">{String(etapa).padStart(2, "0")}</span>
                      {ETAPA_LABEL[etapa]}
                    </span>
                    <span className="rounded-full bg-superficie px-2 py-0.5 text-xs font-bold text-cinza tabular-nums">{itens.length}</span>
                  </div>
                  <div className="min-h-24 space-y-2 p-2.5 pt-0.5">
                    {itens.map((p) => (
                      <div key={p.id} draggable={s.podeEscrever} data-cartao-kanban={p.id}
                        onDragStart={() => setArrastando(p.id)} onDragEnd={() => { setArrastando(null); setAlvo(null); }}
                        className={`surface p-3 transition ${s.podeEscrever ? "cursor-grab active:cursor-grabbing" : ""} ${arrastando === p.id ? "opacity-50" : ""}`}>
                        <div className="flex items-start gap-1.5">
                          {s.podeEscrever && <IconeAlca className="mt-0.5 size-3.5 shrink-0 text-cinza" />}
                          <div className="min-w-0">
                            <Link to={`${RAIZ}/pericias/${p.id}`} className="block truncate text-sm font-semibold text-titulo hover:text-link hover:underline">{nomeDaPericia(p)}</Link>
                            <div className="truncate text-xs text-cinza tabular-nums">{p.numeroProcesso}</div>
                            <div className="truncate text-xs text-cinza">{p.tribunal}</div>
                            {p.proximaAcao && <div className="mt-1.5 line-clamp-2 text-xs text-tinta">{p.proximaAcao}</div>}
                            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                              <span className="text-xs font-semibold text-cinza tabular-nums">{etapa}/{TOTAL_ETAPAS}</span>
                              <Chip tom={PRIORIDADE_TOM[p.prioridade]}>{PRIORIDADE_LABEL[p.prioridade]}</Chip>
                              {totais(p).total > 0 && <span className="text-xs text-cinza tabular-nums">{formatBRL(totais(p).total)}</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {itens.length === 0 && <p className="rounded-xl border border-dashed border-borda py-5 text-center text-xs text-cinza">{s.podeEscrever ? "Arraste uma perícia para cá" : "Nenhuma perícia"}</p>}
                  </div>
                </section>
              );
            })}
          </div>
          {s.podeEscrever && <p className="mt-2 text-xs text-cinza">Arraste o cartão para a etapa. Pelo teclado, mude a etapa na ficha da perícia.</p>}
        </div>
      )}

      <Dialogo aberto={salvarAberto} aoFechar={() => setSalvarAberto(false)} titulo="Salvar o filtro"
        rodape={<>
          <button type="button" className={BOTAO_SECUNDARIO} onClick={() => setSalvarAberto(false)}>Cancelar</button>
          <button type="button" className="pill-cta min-h-10 px-5 text-sm" onClick={salvar} data-confirmar-filtro>Salvar</button>
        </>}>
        <Campo rotulo="Nome do filtro">
          <input className={CAMPO} value={nomeFiltro} maxLength={80} autoFocus placeholder="Ex.: Urgentes de laudo"
            onChange={(e) => setNomeFiltro(e.target.value)} onKeyDown={(e) => e.key === "Enter" && salvar()} data-nome-filtro />
        </Campo>
      </Dialogo>
    </div>
  );
}
